import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const QUESTIONS = [
  {
    section: 1, dimension: 'freq', sectionTitle: 'How often are you actually using AI?',
    sectionDesc: 'Frequency tells us where the habit currently stands, not where it should be.',
    q: 'How often do you currently use any AI tool for work tasks?',
    sub: 'Think about the last 30 days.',
    options: [
      'Never. I have not used AI for work.',
      'Once or twice total, just experimenting.',
      'A few times a month, but not consistently.',
      'Several times a week for specific tasks.',
      'Daily. It is part of how I work.',
    ]
  },
  {
    section: 1, dimension: 'freq', sectionTitle: 'How often are you actually using AI?',
    sectionDesc: 'Frequency tells us where the habit currently stands, not where it should be.',
    q: 'When you use AI at work, what are you primarily using it for?',
    sub: 'Pick the one that describes your most common use.',
    options: [
      'I have not found a real use case yet.',
      'Writing or editing emails and documents.',
      'Summarizing or researching information.',
      'Multiple use cases across different tasks.',
      'Building workflows, automations, or helping others use it too.',
    ]
  },
  {
    section: 2, dimension: 'conf', sectionTitle: 'How confident are you, really?',
    sectionDesc: 'Confidence is not about knowing everything. It is about whether you trust yourself to figure it out.',
    q: 'When you sit down to use an AI tool for a new task, how do you feel?',
    sub: 'Be honest. There is no wrong answer.',
    options: [
      'Anxious. I am not sure what I am doing and I worry I will mess something up.',
      'Uncertain. I give it a try but I do not have much confidence in the result.',
      'Cautiously optimistic. I figure it out as I go.',
      'Comfortable. I know what I am doing most of the time.',
      'Completely at ease. I know how to get what I need.',
    ]
  },
  {
    section: 2, dimension: 'conf', sectionTitle: 'How confident are you, really?',
    sectionDesc: 'Confidence is not about knowing everything. It is about whether you trust yourself to figure it out.',
    q: 'How would you rate the quality of the results you get from AI tools right now?',
    sub: 'Think about actual outputs you have used, not ideal scenarios.',
    options: [
      'Poor. The results are usually not usable.',
      'Inconsistent. Sometimes good, often not.',
      'Decent. I usually get something I can work with after editing.',
      'Good. The outputs are solid and save me real time.',
      'Excellent. I consistently get high-quality results.',
    ]
  },
  {
    section: 3, dimension: 'range', sectionTitle: 'How broadly are you applying AI?',
    sectionDesc: 'The difference between someone who gets real value from AI and someone who does not is almost always range.',
    q: 'Across all the work you do in a typical week, how many distinct tasks do you currently use AI for?',
    sub: 'Think across writing, research, analysis, communication, planning, and beyond.',
    options: [
      'None. I have not found a real use case yet.',
      'One or two. I have a narrow set of things I use it for.',
      'Three to five. I use it across a handful of different tasks.',
      'Six or more. I am applying it broadly across my role.',
      'It touches almost everything I do. I default to AI as a first step.',
    ]
  },
  {
    section: 3, dimension: 'range', sectionTitle: 'How broadly are you applying AI?',
    sectionDesc: 'The difference between someone who gets real value from AI and someone who does not is almost always range.',
    q: 'Has AI changed how you approach any part of your job, or is it just a faster way to do the same things?',
    sub: 'This is about workflow change, not just speed.',
    options: [
      'It has not changed anything. I do the same work the same way.',
      'Mostly just faster. Same tasks, less time.',
      'A little of both. Some things are genuinely different, most are just faster.',
      'It has meaningfully changed how I approach several parts of my role.',
      'It has fundamentally shifted how I think about my work.',
    ]
  },
  {
    section: 4, dimension: 'adv', sectionTitle: 'Where do you stand on AI?',
    sectionDesc: 'Advocacy tells us whether you are willing to bring others along. Champions are not born, they are developed.',
    q: 'How likely are you to recommend AI tools to a colleague on your team?',
    sub: '1 = Would not recommend at all, 5 = Would recommend enthusiastically.',
    options: [
      'Would not recommend. I am not convinced enough to put my name on it.',
      'Probably not. I am still figuring it out myself.',
      'Maybe. Depends on the person and the task.',
      'Probably yes. I have already mentioned it to a few people.',
      'Enthusiastically and often. I am already the person people ask.',
    ]
  },
  {
    section: 4, dimension: 'adv', sectionTitle: 'Where do you stand on AI?',
    sectionDesc: 'Advocacy tells us whether you are willing to bring others along. Champions are not born, they are developed.',
    q: 'Which statement honestly describes how you feel about AI and your job right now?',
    sub: 'Pick the one that is most true, even if it is uncomfortable.',
    options: [
      'Honestly? I am worried AI could replace parts of what I do.',
      'Skeptical. I do not think it will make much difference for my specific role.',
      'Curious but not there yet. I have not figured out the right use cases.',
      'Intentional. I see the potential and I am starting to use it more strategically.',
      'All in. I think this changes everything and I want to lead it.',
    ]
  },
];

const LEVELS = [
  { min: 4, max: 7, label: 'AI Newcomer', tagline: 'Everyone who is great at this started exactly where you are. The early days of training will feel very hands-on and immediately practical.' },
  { min: 8, max: 11, label: 'Early Explorer', tagline: 'You have dipped your toes in and seen some of what is possible. Now it is time to build the habit.' },
  { min: 12, max: 15, label: 'Developing Practitioner', tagline: 'You are using AI but not yet consistently. The next step is breadth and depth across your role.' },
  { min: 16, max: 18, label: 'Active Practitioner', tagline: 'AI is part of how you work. The opportunity now is to go deeper and move toward workflow reimagination.' },
  { min: 19, max: 20, label: 'AI Champion', tagline: 'You are already operating at the level this program is designed to build. You are a strong candidate to mentor others on your team.' },
];

const DIM_NOTES = {
  freq: ['Never using AI tools', 'Starting to experiment', 'Using sometimes', 'Using regularly', 'Daily AI user'],
  conf: ['Significant confidence gap', 'Early confidence building', 'Growing confidence', 'Mostly confident', 'Fully confident'],
  range: ['No use cases yet', 'Narrow use case set', 'Moderate range', 'Broad application', 'Wide strategic use'],
  adv: ['AI skeptic or worried', 'Cautiously uncertain', 'Curious and open', 'Positive advocate', 'Enthusiastic champion'],
};

export default function Assessment({ user, onNav }) {
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(1);
  const [openText, setOpenText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingPre, setExistingPre] = useState(null);
  const [scores, setScores] = useState(null);
  const [assessmentType, setAssessmentType] = useState('pre');
  const [postUnlocked, setPostUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      const ref = doc(db, 'assessments', user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        if (data.pre) setExistingPre(data.pre);
        if (data.pre && data.post) { setSubmitted(true); setLoading(false); return; }

        // Check if post assessment is unlocked — either engagement-wide or per-participant
        if (data.pre) {
          let unlocked = false;
          // Read user document once and use it for both checks
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          const userData = userSnap.exists() ? userSnap.data() : {};
          // Engagement-wide unlock (set by TCF admin via Engagements page)
          const userCompanyCode = userData.companyCode || userData.engagementId;
          if (userCompanyCode) {
            const engSnap = await getDoc(doc(db, 'engagements', userCompanyCode));
            if (engSnap.exists() && engSnap.data().postAssessmentUnlocked) unlocked = true;
          }
          // Per-participant unlock (set by company admin or TCF admin)
          if (userData.postAssessmentUnlocked) unlocked = true;
          if (unlocked) {
            setPostUnlocked(true);
            setAssessmentType('post');
          }
        }
      }
      setLoading(false);
    };
    check();
  }, [user.uid]);

  const sectionQuestions = QUESTIONS.filter(q => q.section === currentSection);
  const totalSections = 4;
  const progress = (currentSection / (totalSections + 1)) * 100;

  const sectionAnswered = sectionQuestions.every((_, i) => {
    const key = `q${(currentSection - 1) * 2 + i}`;
    return answers[key] !== undefined;
  });

  const handleSelect = (qIndex, value) => {
    setAnswers(prev => ({ ...prev, [`q${qIndex}`]: value }));
  };

  const calcScores = (ans) => {
    const freq = Math.round(((ans.q0 || 0) + (ans.q1 || 0)) / 2);
    const conf = Math.round(((ans.q2 || 0) + (ans.q3 || 0)) / 2);
    const range = Math.round(((ans.q4 || 0) + (ans.q5 || 0)) / 2);
    const adv = Math.round(((ans.q6 || 0) + (ans.q7 || 0)) / 2);
    const total = freq + conf + range + adv;
    return { freq, conf, range, adv, total };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const sc = calcScores(answers);
    setScores(sc);
    const level = LEVELS.find(l => sc.total >= l.min && sc.total <= l.max) || LEVELS[0];

    const payload = {
      scores: sc,
      level: level.label,
      openText,
      completedAt: serverTimestamp(),
    };

    const ref = doc(db, 'assessments', user.uid);
    const existing = (await getDoc(ref)).data() || {};

    await setDoc(ref, {
      ...existing,
      userId: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      [assessmentType]: payload,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    setSubmitted(true);
    setSubmitting(false);
  };

  if (loading) return <div style={{ color: '#8A8A8A', fontSize: '14px' }}>Loading...</div>;

  // Has pre score but post is NOT unlocked yet
  if (existingPre && !postUnlocked && !submitted) {
    return (
      <div style={{ maxWidth: '560px' }}>
        <div style={styles.sectionTitle}>Your Baseline Is Recorded</div>
        <div style={styles.sectionDesc} style={{ marginBottom: '24px' }}>
          You completed your pre-training assessment. Your post-training assessment will be unlocked by your facilitator after the program concludes. Check back then to see how far you have come.
        </div>
        <div style={styles.lockedCard}>
          <div style={styles.lockedIcon}>🔒</div>
          <div style={styles.lockedTitle}>Post-Training Assessment</div>
          <div style={styles.lockedText}>Your facilitator will unlock this when it is time. You will see it here automatically.</div>
        </div>
        <button className="btn-primary" style={{ marginTop: '24px' }} onClick={() => onNav('dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  if (submitted && scores) return <Results scores={scores} existingPre={existingPre} assessmentType={assessmentType} onNav={onNav} />;

  if (submitted && !scores) {
    return (
      <div>
        <div className="page-heading">Assessment Complete</div>
        <div className="page-sub">Both assessments are complete. Check your dashboard to see your full results.</div>
        <button className="btn-primary" onClick={() => onNav('dashboard')}>Go to Dashboard</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={styles.snapshotHeader}>
        <div style={styles.snapshotLabel}>
          {assessmentType === 'post' ? 'AI Readiness Snapshot · Post-Training' : 'AI Readiness Snapshot · Pre-Training'}
        </div>
        <div style={styles.snapshotSub}>
          {assessmentType === 'post'
            ? 'Captures where you ended up after training. Same eight questions as before so we can measure your growth.'
            : 'Captures where you are starting. Eight honest questions, no right answers.'}
        </div>
      </div>

      <div className="progress-strip"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>

      <div style={styles.sectionNav}>
        {[1,2,3,4].map(s => (
          <div key={s} style={{
            ...styles.snavItem,
            ...(s < currentSection ? styles.snavDone : {}),
            ...(s === currentSection ? styles.snavActive : {}),
          }}>
            {s === 1 ? 'Frequency' : s === 2 ? 'Confidence' : s === 3 ? 'Use Case Range' : 'Advocacy'}
          </div>
        ))}
      </div>

      <div style={styles.sectionLabel}>Section {currentSection} of {totalSections}</div>
      <div style={styles.sectionTitle}>{sectionQuestions[0]?.sectionTitle}</div>
      <div style={styles.sectionDesc}>{sectionQuestions[0]?.sectionDesc}</div>

      {sectionQuestions.map((q, i) => {
        const qIndex = (currentSection - 1) * 2 + i;
        return (
          <div key={qIndex} style={styles.qBlock}>
            <div style={styles.qText}>{q.q}</div>
            {q.sub && <div style={styles.qSub}>{q.sub}</div>}
            <div style={styles.qOptions}>
              {q.options.map((opt, vi) => (
                <label key={vi} style={{
                  ...styles.qOpt,
                  ...(answers[`q${qIndex}`] === vi + 1 ? styles.qOptSelected : {}),
                }}>
                  <input
                    type="radio"
                    name={`q${qIndex}`}
                    value={vi + 1}
                    checked={answers[`q${qIndex}`] === vi + 1}
                    onChange={() => handleSelect(qIndex, vi + 1)}
                    style={{ accentColor: '#1B2A4A', flexShrink: 0 }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        );
      })}

      {currentSection === 4 && assessmentType === 'pre' && (
        <div style={styles.qBlock}>
          <div style={styles.qText}>Anything else you want your facilitator to know?</div>
          <div style={styles.qSub}>Optional. This goes directly to your facilitator and helps personalize your training experience. Other participants cannot see this.</div>
          <textarea
            style={styles.textarea}
            value={openText}
            onChange={e => setOpenText(e.target.value)}
            placeholder="Concerns, specific things you want to learn, context about your role..."
          />
        </div>
      )}

      <div style={styles.btnRow}>
        {currentSection > 1 && (
          <button className="btn-secondary" onClick={() => setCurrentSection(s => s - 1)}>← Back</button>
        )}
        {currentSection < 4 ? (
          <button
            className="btn-primary"
            onClick={() => { if (sectionAnswered) setCurrentSection(s => s + 1); }}
            style={{ opacity: sectionAnswered ? 1 : 0.5 }}
          >
            Next Section →
          </button>
        ) : (
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!sectionAnswered || submitting}
            style={{ opacity: sectionAnswered ? 1 : 0.5 }}
          >
            {submitting ? 'Saving...' : 'See My Results →'}
          </button>
        )}
      </div>
    </div>
  );
}

function Results({ scores, existingPre, assessmentType, onNav }) {
  const level = LEVELS.find(l => scores.total >= l.min && scores.total <= l.max) || LEVELS[0];
  const dims = [
    { key: 'freq', label: 'Frequency' },
    { key: 'conf', label: 'Confidence' },
    { key: 'range', label: 'Use Case Range' },
    { key: 'adv', label: 'Advocacy' },
  ];

  return (
    <div>
      <div style={styles.resultsHero}>
        <div style={styles.resultsBadge}>{scores.total}</div>
        <div style={styles.resultsLevel}>{level.label}</div>
        <div style={styles.resultsTagline}>{level.tagline}</div>
      </div>

      <div className="dim-grid" style={{ marginTop: '24px' }}>
        {dims.map(d => (
          <div className="dim-card" key={d.key}>
            <div className="dim-name">{d.label}</div>
            <div className="dim-score">{scores[d.key]}<span> /5</span></div>
            <div className="dim-bar"><div className="dim-fill" style={{ width: `${((scores[d.key]-1)/4)*100}%` }} /></div>
            <div className="dim-label">{DIM_NOTES[d.key][(scores[d.key] || 1) - 1]}</div>
            {existingPre && assessmentType === 'post' && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#1a8a4a', fontWeight: '600' }}>
                {scores[d.key] > existingPre.scores[d.key] ? `+${scores[d.key] - existingPre.scores[d.key]} from baseline` : 'Same as baseline'}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={styles.trainingPath}>
        <div style={styles.tpLabel}>Your Training Path</div>
        <div style={styles.tpText}>
          {scores.total <= 7 && "Your training path will focus heavily on reducing friction and building your first successful habits. Expect the early days to feel very hands-on and immediately practical."}
          {scores.total >= 8 && scores.total <= 11 && "The early days of training will focus on turning sporadic use into consistent habit. You will leave with a personal prompt library built for your specific role."}
          {scores.total >= 12 && scores.total <= 15 && "You are in the sweet spot for rapid growth. The training will expand your use cases significantly and help you get dramatically better results."}
          {scores.total >= 16 && scores.total <= 18 && "You are ready for depth. The training will push you beyond individual tasks toward workflow reimagination."}
          {scores.total >= 19 && "Standard program content will be review territory for you. Your path leads directly toward becoming an internal champion who mentors others on your team."}
        </div>
      </div>

      {assessmentType === 'pre' ? (
        <>
          <div style={styles.nextSteps}>
            <div style={styles.nextTitle}>What Happens Next</div>
            {[
              'Your facilitator sees your results before training begins. The session will be shaped around where this group actually is.',
              'You will take this same assessment again after the program completes. That comparison is how we prove the training actually worked.',
              'Your workbooks are now unlocked in the portal. You can access and fill them out at any time during your 12-month access window.',
            ].map((text, i) => (
              <div key={i} style={styles.nextItem}>
                <div style={styles.nextNum}>{i + 1}</div>
                <div>{text}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button className="btn-primary" onClick={() => onNav('dashboard')}>Go to Dashboard</button>
            <button className="btn-secondary" onClick={() => onNav('workbooks')}>Open Workbooks</button>
          </div>
        </>
      ) : (
        <>
          <div style={styles.nextSteps}>
            <div style={styles.nextTitle}>Post-Assessment Locked In</div>
            <div style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '16px' }}>
              Your post-training scores are saved. You can see how far you moved in the comparison above. One last thing stands between you and your certificate, the Final Exam.
            </div>
            <div style={styles.nextItem}>
              <div style={styles.nextNum}>1</div>
              <div>Compare your pre and post scores above to see exactly how far you moved across each dimension.</div>
            </div>
            <div style={styles.nextItem}>
              <div style={styles.nextNum}>2</div>
              <div>Head to the Final Exam when you are ready. Four questions about what you built. Pass and your certificate lands in Account Settings.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={() => onNav('exam')}>Take the Final Exam →</button>
            <button className="btn-secondary" onClick={() => onNav('dashboard')}>Back to Dashboard</button>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  postBanner: { background: '#D4A847', color: '#1B2A4A', padding: '10px 16px', fontSize: '13px', fontWeight: '600', marginBottom: '20px', textAlign: 'center' },
  snapshotHeader: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '24px' },
  snapshotLabel: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: '#D4A847', marginBottom: '6px' },
  snapshotSub: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.5', fontWeight: '300' },
  lockedCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '32px', textAlign: 'center' },
  lockedIcon: { fontSize: '32px', marginBottom: '12px' },
  lockedTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', marginBottom: '8px' },
  lockedText: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', lineHeight: '1.6' },
  sectionNav: { display: 'flex', marginBottom: '28px' },
  snavItem: { flex: 1, padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', borderBottom: '2px solid #E8E6E2', color: '#8A8A8A' },
  snavDone: { borderBottomColor: '#D4A847', color: '#D4A847' },
  snavActive: { borderBottomColor: '#1B2A4A', color: '#1B2A4A' },
  sectionLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#C0392B', marginBottom: '4px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#1B2A4A', marginBottom: '6px' },
  sectionDesc: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', marginBottom: '24px', lineHeight: '1.6' },
  qBlock: { marginBottom: '24px' },
  qText: { fontSize: '14px', fontWeight: '500', color: '#3D3D3D', marginBottom: '4px', lineHeight: '1.5' },
  qSub: { fontSize: '12px', color: '#8A8A8A', marginBottom: '12px', fontWeight: '300' },
  qOptions: { display: 'flex', flexDirection: 'column', gap: '6px' },
  qOpt: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', background: '#FFFFFF', border: '1.5px solid #E8E6E2', cursor: 'pointer', fontSize: '13px', lineHeight: '1.4', transition: 'border-color 0.15s' },
  qOptSelected: { borderColor: '#1B2A4A', background: 'rgba(27,42,74,0.03)' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '12px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FFFFFF', outline: 'none', resize: 'vertical', minHeight: '90px', fontWeight: '300' },
  btnRow: { display: 'flex', gap: '12px', marginTop: '28px' },
  resultsHero: { background: '#1B2A4A', padding: '36px', textAlign: 'center', marginBottom: '0' },
  resultsBadge: { display: 'inline-block', background: 'rgba(212,168,71,0.15)', border: '2px solid #D4A847', width: '110px', height: '110px', lineHeight: '110px', fontFamily: 'Playfair Display, serif', fontSize: '60px', fontWeight: '900', color: '#fff', marginBottom: '16px' },
  resultsLevel: { fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#D4A847', marginBottom: '8px' },
  resultsTagline: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: '300', maxWidth: '480px', margin: '0 auto', lineHeight: '1.7' },
  trainingPath: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '16px' },
  tpLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  tpText: { fontSize: '14px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', lineHeight: '1.7' },
  nextSteps: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px' },
  nextTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1B2A4A', marginBottom: '16px' },
  nextItem: { display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '14px', fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6' },
  nextNum: { width: '24px', height: '24px', background: '#C0392B', color: '#fff', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};
